import React, { useState, useEffect, useCallback } from 'react';
import { capitalizeWords, formatDate } from '../utils/format';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { candidateAPI } from '../services/api';
import Navigation from '../components/Navigation';
import './CandidateList.css';
import PlusIcon from '../components/PlusIcon';

const VIEWS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

// Terminal statuses archive automatically, so the server always includes archived
// candidates when one is selected. Mirrored here only to explain that in the UI.
const TERMINAL_STATUSES = ['recruited', 'rejected', 'offer_declined'];

function CandidateList({ onLogout, onFeedback }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // Filters live in the URL so the dashboard cards can link straight to a view
  // and so a filtered list stays shareable / survives a refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';
  const view = VIEWS.some(v => v.value === searchParams.get('view'))
    ? searchParams.get('view')
    : 'active';

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const loadCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await candidateAPI.list(statusFilter || null, view);
      setCandidates(data);
    } catch (error) {
      console.error('Error loading candidates:', error);
      alert('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, view]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        await candidateAPI.delete(id);
        loadCandidates();
      } catch (error) {
        console.error('Error deleting candidate:', error);
        alert('Failed to delete candidate');
      }
    }
  };

  return (
    <div>
      <Navigation onLogout={onLogout} onFeedback={onFeedback} />
      <div className="container container--wide">
        <div className="page-header">
          <h1>All Candidates</h1>
          <button
            onClick={() => navigate('/candidates/new')}
            className="btn-primary btn-add-candidate next-glow next-glow-soft"
          >
            <PlusIcon />
            Add New Candidate
          </button>
        </div>

        <div className="filter-section">
          <span className="filter-label">Show:</span>
          <div className="view-toggle" role="group" aria-label="Show candidates">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                className={`view-pill${view === v.value ? ' is-selected' : ''}`}
                aria-pressed={view === v.value}
                onClick={() => setParam('view', v.value === 'active' ? null : v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>

          <label htmlFor="status-filter">Filter by Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setParam('status', e.target.value)}
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="cv_received">CV Received</option>
            <option value="background_check">Background Check</option>
            <option value="phone_interview_scheduled">Phone Interview Scheduled</option>
            <option value="phone_interview_completed">Phone Interview Completed</option>
            <option value="in_person_interview_scheduled">In-Person Interview Scheduled</option>
            <option value="in_person_interview_completed">In-Person Interview Completed</option>
            <option value="trial_day_scheduled">Trial Day Scheduled</option>
            <option value="trial_day_completed">Trial Day Completed</option>
            <option value="offer_sent">Job Offer Sent</option>
            <option value="offer_accepted">Offer Accepted</option>
            <option value="offer_declined">Job Offer Declined</option>
            <option value="references_requested">References Requested</option>
            <option value="reference_received">Reference Received</option>
            <option value="referee_details_received">Referee Details Received</option>
            <option value="documents_email_sent">Documents Email Sent</option>
            <option value="all_starter_docs_received">All Starter Docs Received</option>
            <option value="contract_email_sent">Contract Email Sent</option>
            <option value="awaiting_contract_signature">Awaiting Contract Signature</option>
            <option value="candidate_signed">Candidate Signed Contract</option>
            <option value="contract_signed">Contract Signed</option>
            <option value="onboarding_checklist_complete">Onboarding Checklist Complete</option>
            <option value="awaiting_induction_signatures">Awaiting Induction Signatures</option>
            <option value="induction_folder_complete">Induction Folder Complete</option>
            <option value="recruited">Recruited !</option>
            <option value="rejected">Rejected</option>
          </select>

          {TERMINAL_STATUSES.includes(statusFilter) && (
            <span className="filter-hint">Includes archived</span>
          )}
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : candidates.length > 0 ? (
          <table className="candidate-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Position</th>
                <th>Source</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => (
                <tr key={candidate.id} className={candidate.archived_date ? 'is-archived' : ''}>
                  <td className="nowrap">{`${candidate.first_name} ${candidate.last_name}`}</td>
                  <td>{candidate.email}</td>
                  <td className="nowrap">{candidate.phone || '-'}</td>
                  <td className="nowrap">
                    {capitalizeWords(candidate.position_applied)}
                    {/* A progression is the same person re-hired into a new role on a
                        linked record; the original shows where they went. */}
                    {candidate.is_progression && (
                      <span className="status-badge status-progression" title={`Progression from ${candidate.progressed_from_position || 'a previous position'}`}>
                        Progression
                      </span>
                    )}
                    {candidate.progressed_to && (
                      <span className="status-badge status-progression" title={`Progressed to ${candidate.progressed_to.position || 'RVN'} — see that record`}>
                        → {candidate.progressed_to.position || 'RVN'}
                      </span>
                    )}
                  </td>
                  <td className="nowrap">{capitalizeWords(candidate.source)}</td>
                  <td className="nowrap">
                    <span className={`status-badge status-${candidate.status}`}>
                      {candidate.status === 'offer_sent' ? 'job offer sent' : candidate.status === 'candidate_signed' ? 'candidate signed contract' : candidate.status === 'recruited' ? 'Recruited !' : candidate.status === 'background_check' ? 'background checked' : candidate.status.replace(/_/g, ' ')}
                    </span>
                    {candidate.archived_date && (
                      <span
                        className="status-badge status-archived"
                        title={`Archived ${formatDate(candidate.archived_date)}`}
                      >
                        Archived
                      </span>
                    )}
                  </td>
                  <td className="nowrap">{formatDate(candidate.created_at)}</td>
                  <td className="nowrap">
                    <div className="row-actions">
                      <button
                        onClick={() => navigate(`/candidates/${candidate.id}`)}
                        className="btn-small btn-view"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleDelete(candidate.id, `${candidate.first_name} ${candidate.last_name}`)}
                        className="btn-small btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : view === 'archived' ? (
          <p>No archived candidates.</p>
        ) : (
          <p>No candidates found.</p>
        )}
      </div>
    </div>
  );
}

export default CandidateList;
